import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLazyQuery} from '@apollo/client';
import {
    Button,
    Card,
    Col,
    Drawer,
    Image,
    Input,
    message,
    Radio,
    Row,
    Skeleton,
    Space,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {showError} from '../../../../utils/exceptionUtils';
import {
    GET_REVIEW_TASK_DETAIL,
    GetReviewTaskDetailResponse,
    GetReviewTaskDetailVariables,
} from '../graphql/queries/getReviewTaskDetail';
import {useReviewTaskActions} from '../hooks/useReviewTaskActions';
import {useReviewTaskCanOperate} from '../hooks/useReviewTaskPermission';
import {
    pickRejectReasonText,
    ReviewRejectReasonOption,
    useReviewApproveReasons,
    useReviewRejectReasons,
} from '../graphql/queries/getReviewRejectReasons';
import {
    pickCategoryText,
    useComplaintCategories,
} from '../graphql/queries/getComplaintCategories';
import {
    ComplaintAppealDetail,
    ComplaintReasonParsed,
    CustomerComplaintDetail,
    ReviewStatus,
    ReviewTaskListItem,
    ReviewTaskLog,
} from '../graphql/types/ReviewTaskType';
import {normalizeLang, pickDisplayText} from '../../../../utils/i18nText';
import AttachmentTile from '../../../../components/AttachmentTile';
import RichTextView from '../../../../components/RichTextView';
import ReviewTaskTimelineDrawer from './ReviewTaskTimelineDrawer';

const {Text} = Typography;
const {TextArea} = Input;

/**
 * 客诉原因展示：默认按当前 locale 显示译文，提供「显示原文」按钮原地切换为原文（complaintReason.text）。
 *
 * 译文可能因截取 / 符号转义异常而失真，审核员需要核对原文；
 * 当译文与原文相同时（源语言==当前 locale 或无译文，pickDisplayText 取出的就是 text），切换无意义，隐藏按钮。
 */
const ComplaintReasonView: React.FC<{
    reason: ComplaintReasonParsed;
    currentLocale: string;
    label: string;
    showOriginalLabel: string;
    showTranslationLabel: string;
}> = ({reason, currentLocale, label, showOriginalLabel, showTranslationLabel}) => {
    const [showOriginal, setShowOriginal] = useState(false);
    const translated = pickDisplayText(reason, currentLocale);
    const original = reason.text || '';
    // 是否存在当前 locale 的真实译文：与 pickDisplayText 同口径（命中 translations 即真译文，否则是原文兜底）。
    // 注意：后端 translations 只含「目标语言译文」，不含源语言/原文，且译文经过标点规整（与原文不必逐字相同）；
    // 因此判断「是否兜底」必须看 translations 是否命中 locale，不能用「译文==原文」字符串比较（既会误判，也不符数据事实）。
    const hasTranslation = !!reason.translations?.find((e) => e.lang === currentLocale)?.text;
    // 仅当存在真实译文（即当前展示的是译文、与原文不同源）时，切换原文才有意义
    const hasDistinctOriginal = hasTranslation && !!original;
    const displayText = showOriginal ? original : translated;

    return (
        <div className="mt-3">
            <div className="flex items-center justify-between">
                <Text type="secondary" className="text-sm">
                    {label}:
                </Text>
                {hasDistinctOriginal && (
                    <Button
                        type="link"
                        size="small"
                        className="px-0"
                        onClick={() => setShowOriginal((v) => !v)}
                    >
                        {showOriginal ? showTranslationLabel : showOriginalLabel}
                    </Button>
                )}
            </div>
            <RichTextView
                html={displayText}
                className="text-sm mt-1 p-3 [&_img]:max-w-full"
                style={{
                    background: '#fafafa',
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                }}
            />
        </div>
    );
};

const COMMENT_MAX = 120;
/** 驳回理由字典中的「其他」code，选中时处理说明必填 */
const REJECT_REASON_OTHER = 'OTHER';

/** 审核评论 JSON schema 当前版本 */
const REVIEW_COMMENT_JSON_SCHEMA_VERSION = 1;

interface ReviewCommentJson {
    schemaVersion: number;
    lang: string;
    tagKeys: string[];
    text: string;
    /** 自由文本翻译，{en: "...", es: "..."}。提交时由 api-server 调阿里云填充 */
    translations?: Record<string, string>;
}

/** 提交侧序列化：UI state 拼成 ReviewCommentJson 字符串。tagKeys/text 都为空时返回 null */
const buildReviewCommentJson = (
    tagKeys: string[],
    text: string,
    lang: string
): string | null => {
    const trimmedText = text.trim();
    if (tagKeys.length === 0 && !trimmedText) return null;
    const payload: ReviewCommentJson = {
        schemaVersion: REVIEW_COMMENT_JSON_SCHEMA_VERSION,
        lang,
        tagKeys,
        text: trimmedText,
    };
    return JSON.stringify(payload);
};

interface ReviewTaskDetailDrawerProps {
    open: boolean;
    /**
     * 待查看的工单 ID 数组：单条审核传 [id]；批量审核传选中的 N 条 ID。
     * 抽屉只持有 ID，每次推进都按 ID 重新查询详情，避免使用列表快照导致脏读。
     */
    reviewTaskIds: number[];
    onClose: () => void;
    onReviewed: () => void;
}


/** 单行键值对 row，左标签右内容 */
const InfoRow: React.FC<{label: string; children: React.ReactNode; labelSpan?: number}> = ({
    label,
    children,
    labelSpan = 6,
}) => (
    <Row className="mb-2">
        <Col span={labelSpan}>
            <Text type="secondary" className="text-sm">{label}:</Text>
        </Col>
        <Col span={24 - labelSpan}>
            <span className="text-sm">{children ?? '--'}</span>
        </Col>
    </Row>
);

const ReviewTaskDetailDrawer: React.FC<ReviewTaskDetailDrawerProps> = ({
    open,
    reviewTaskIds,
    onClose,
    onReviewed,
}) => {
    const {t, i18n} = useTranslation();

    const [comment, setComment] = useState('');
    const [reviewAction, setReviewAction] = useState<'APPROVE' | 'REJECT' | undefined>();
    const [selectedReasonKeys, setSelectedReasonKeys] = useState<string[]>([]);
    const [commentComposerFocused, setCommentComposerFocused] = useState(false);
    const [timelineVisible, setTimelineVisible] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const {approve, reject, loading: actionLoading} = useReviewTaskActions();
    const canOperate = useReviewTaskCanOperate();

    const isBatchMode = reviewTaskIds.length > 1;
    const currentTaskId: number | undefined = reviewTaskIds[currentIndex];

    const [fetchDetail, {data: detailData, loading: detailLoading}] = useLazyQuery<
        GetReviewTaskDetailResponse,
        GetReviewTaskDetailVariables
    >(GET_REVIEW_TASK_DETAIL, {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    });

    // 抽屉打开 或 reviewTaskIds 变化时重置 index
    const prevIdsKeyRef = useRef<string>('');
    useEffect(() => {
        if (!open) return;
        const idsKey = reviewTaskIds.join(',');
        if (prevIdsKeyRef.current !== idsKey) {
            prevIdsKeyRef.current = idsKey;
            setCurrentIndex(0);
        }
    }, [open, reviewTaskIds]);

    // 抽屉关闭时清理本地引用，下次打开能重新触发 index reset
    useEffect(() => {
        if (!open) {
            prevIdsKeyRef.current = '';
        }
    }, [open]);

    // 当前 ID 变化或抽屉打开时，按 ID 拉取最新详情（不依赖列表快照，避免脏读）
    useEffect(() => {
        if (open && currentTaskId) {
            setComment('');
            setReviewAction(undefined);
            setSelectedReasonKeys([]);
            fetchDetail({variables: {reviewTaskId: currentTaskId}});
        }
    }, [open, currentTaskId, fetchDetail]);

    // useLazyQuery 的 detailData 是上一次查询结果：切换工单时新查询返回前 detailData 仍是上一条，
    // 仅当返回的 reviewTaskId 与当前 currentTaskId 匹配才采用，避免渲染上一条工单的残留数据
    const rawDetail = detailData?.getReviewTaskDetailById;
    const detail =
        rawDetail && rawDetail.reviewTask?.reviewTaskId === currentTaskId ? rawDetail : undefined;
    const task = detail?.reviewTask;
    const logs: ReviewTaskLog[] = detail?.logs ?? [];
    const appeal: ComplaintAppealDetail | undefined = detail?.complaintAppeal;
    const complaint: CustomerComplaintDetail | undefined = detail?.complaint;
    const orderDetail = detail?.orderDetail;

    const attachments = appeal?.appealAttachmentS3UrlsJson ?? [];

    // 客诉方上传的证据文件：evidenceFilesJson 为 [{file_name,file_type,file_url}] 的 JSON 串，取 file_url 渲染
    const complaintEvidenceUrls = useMemo(() => {
        if (!complaint?.evidenceFilesJson) return [];
        try {
            const files = JSON.parse(complaint.evidenceFilesJson) as Array<{file_url?: string}>;
            return files.map((f) => f.file_url).filter((url): url is string => !!url);
        } catch {
            return [];
        }
    }, [complaint?.evidenceFilesJson]);

    /** 可审核 = 工单待审 AND 有审核操作权限（资源码 CS_DSP_APPEAL_REVIEW_OPERATE）。
     *  无操作权限时待审工单不出审核区；但已审核工单的 readonly 回显仍保留——
     *  查看历史审核结论属查看能力，不应被操作权限剥夺 */
    const canReview = task?.status === ReviewStatus.PENDING_REVIEW && canOperate;
    /** 已审核：以 task.status 为唯一可信源（仅 APPROVED / REJECTED 两态算审核员审完）；
     *  CANCELLED 由 DSP 取消，不属于审核结果。不依赖可空的 appeal.reviewResult，
     *  避免无申诉记录或后端未返回该字段时误判为"未审核"而隐藏审核区 */
    const reviewed =
        task?.status === ReviewStatus.APPROVED || task?.status === ReviewStatus.REJECTED;
    /** 只读回显：已审核工单，审核区组件保留布局但禁用所有交互、隐藏提交按钮。
     *  CANCELLED 工单未经审核，不渲染审核区——取消信息由操作日志抽屉查看 */
    const readonly = !canReview && reviewed;

    /** 已审核工单回填审核区 state：审核结果优先取 appeal.reviewResult，回退 task.status（与 reviewed 口径一致）。
     *  自由文本按当前 locale 优先取 translations[locale]，回退到原文 */
    const reviewResult = appeal?.reviewResult ?? task?.status;
    const reviewComment = appeal?.reviewComment;
    const currentLocale = normalizeLang(i18n.language);
    /** 后端字典 —— 字典 + 三语文案完全由后端拥有，前端只读 */
    const {options: rejectReasons} = useReviewRejectReasons();
    const {options: approveReasons} = useReviewApproveReasons();
    /** 当前审核动作对应的可选理由字典 */
    const activeReasons = reviewAction === 'APPROVE' ? approveReasons : rejectReasons;
    /** 理由 code → 字典项，用于已选 chips 取文案。只取当前动作的字典，避免两套字典同名 code 互相覆盖；
     *  readonly 回显以后端解析的 reviewComment.tagOptions 优先——它覆盖历史已停用 code
     *  （字典接口只下发当前可选项） */
    const reasonByCode = useMemo(() => {
        const m = new Map<string, ReviewRejectReasonOption>();
        activeReasons.forEach((o) => m.set(o.code, o));
        if (readonly) {
            reviewComment?.tagOptions?.forEach((o) => m.set(o.code, o));
        }
        return m;
    }, [activeReasons, readonly, reviewComment]);
    /** 客诉一级分类字典（投诉类型展示用）—— 字典 + 三语文案完全由后端拥有 */
    const {options: complaintCategories} = useComplaintCategories();
    const categoryByCode = useMemo(
        () => new Map(complaintCategories.map((o) => [o.code, o])),
        [complaintCategories],
    );
    useEffect(() => {
        if (!readonly) return;
        const resultToAction: Record<string, 'APPROVE' | 'REJECT'> = {
            APPROVED: 'APPROVE',
            REJECTED: 'REJECT',
        };
        setReviewAction(resultToAction[reviewResult ?? ''] ?? undefined);
        setSelectedReasonKeys(reviewComment?.tagKeys ?? []);
        setComment(pickDisplayText(reviewComment, currentLocale));
    }, [readonly, reviewResult, reviewComment, currentLocale]);

    /** 切换某个快捷理由 Tag 的选中状态（点 Tag 切换；textarea 内容不受影响） */
    const toggleReasonKey = (key: string) => {
        setSelectedReasonKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    /** 驳回且选了「其他」：处理说明必填 */
    const rejectNeedsComment =
        reviewAction === 'REJECT' && selectedReasonKeys.includes(REJECT_REASON_OTHER);

    /** readonly 回显时是否有可展示的评论（已映射出审核动作，且有理由 Tag 或自由文本）；否则整行隐藏 */
    const readonlyHasComment =
        !!reviewAction &&
        ((reviewComment?.tagKeys?.length ?? 0) > 0 || !!reviewComment?.text);

    const textareaMaxLength = COMMENT_MAX;

    const isLastTask = isBatchMode && currentIndex === reviewTaskIds.length - 1;

    /** 推进到下一条；末条后静默关闭抽屉 */
    const moveToNext = () => {
        const nextIndex = currentIndex + 1;
        if (nextIndex < reviewTaskIds.length) {
            setCurrentIndex(nextIndex);
        } else {
            onClose();
        }
    };

    const handleSkip = () => {
        moveToNext();
    };

    const handleSubmit = async () => {
        if (!currentTaskId) return;
        if (!reviewAction) {
            message.warning(t('reviewTask.detail.actionRequired'));
            return;
        }
        if (comment.length > COMMENT_MAX) {
            message.warning(
                t('reviewTask.detail.commentLengthHint', {max: COMMENT_MAX})
            );
            return;
        }
        if (reviewAction === 'REJECT') {
            // 驳回：必选理由 Tag，选「其他」时处理说明必填
            if (selectedReasonKeys.length === 0) {
                message.warning(t('reviewTask.detail.rejectReasonRequired'));
                return;
            }
            if (rejectNeedsComment && !comment.trim()) {
                message.warning(t('reviewTask.detail.otherCommentRequired'));
                return;
            }
        } else if (selectedReasonKeys.length === 0 && !comment.trim()) {
            // 通过：理由 Tag 或自由文本至少其一
            message.warning(t('reviewTask.detail.commentRequired'));
            return;
        }
        const commentJsonToSend = buildReviewCommentJson(
            selectedReasonKeys,
            comment,
            normalizeLang(i18n.language),
        );
        try {
            if (reviewAction === 'APPROVE') {
                await approve(currentTaskId, commentJsonToSend);
            } else {
                await reject(currentTaskId, commentJsonToSend);
            }
            message.success(t('reviewTask.detail.reviewSuccess'));
            onReviewed();
            moveToNext();
        } catch (e) {
            showError(e);
        }
    };

    return (
        <Drawer
            title={
                <div className="flex items-center">
                    <CloseOutlined
                        className="mr-3 cursor-pointer hover:text-gray-600"
                        onClick={onClose}
                    />
                    {isBatchMode ? (
                        <span className="text-lg font-medium">
                            {t('reviewTask.detail.batchReviewTitle', {
                                current: currentIndex + 1,
                                total: reviewTaskIds.length,
                            })}
                        </span>
                    ) : (
                        <span className="text-lg font-medium">
                            {task?.trackingNumber ?? '--'}
                        </span>
                    )}
                </div>
            }
            width="calc(100vw - 304px)"
            open={open}
            onClose={onClose}
            destroyOnClose
            closable={false}
            extra={
                isBatchMode && isLastTask ? (
                    <span className="text-sm text-gray-500">
                        {t('reviewTask.detail.noMoreTasks')}
                    </span>
                ) : null
            }
            styles={{
                body: {padding: '16px 32px', background: '#fff'},
                header: {borderBottom: '1px solid #f0f0f0'},
            }}
        >
            {detailLoading ? (
                <Skeleton active paragraph={{rows: 10}} />
            ) : (
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    {/* 顶部概览卡片：任务ID / 运单号 / 司机ID / 申诉时间 */}
                    <div
                        style={{
                            background: '#fafafa',
                            border: '1px solid #f0f0f0',
                            borderRadius: 6,
                            padding: '16px 24px',
                        }}
                    >
                        <Row gutter={32}>
                            <Col flex="1">
                                <div className="text-sm text-gray-500 mb-1">
                                    {t('reviewTask.detail.trackingNumber')}
                                </div>
                                {task?.trackingNumber ? (
                                    <span
                                        className="text-lg font-medium font-mono text-purple-600 cursor-pointer hover:text-purple-800 hover:underline"
                                        onClick={() => setTimelineVisible(true)}
                                    >
                                        {task.trackingNumber}
                                    </span>
                                ) : (
                                    <div className="text-lg font-medium font-mono">--</div>
                                )}
                            </Col>
                            <Col flex="1">
                                <div className="text-sm text-gray-500 mb-1">
                                    {t('reviewTask.detail.bizTypeAndSubType')}
                                </div>
                                <div className="text-lg font-medium">
                                    {task?.bizType ? (
                                        <>
                                            {t(`reviewTask.bizType.${task.bizType}`)}
                                            {complaint?.complaintType && (
                                                <>
                                                    <span className="mx-1 text-gray-400">/</span>
                                                    {t(
                                                        `customerComplaint.type.${complaint.complaintType}`,
                                                        complaint.complaintType
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        '--'
                                    )}
                                </div>
                            </Col>
                            <Col flex="1">
                                <div className="text-sm text-gray-500 mb-1">
                                    {t('reviewTask.detail.dsp')}
                                </div>
                                <div className="text-lg font-medium">
                                    {task?.dspName || '--'}
                                </div>
                            </Col>
                            <Col flex="1">
                                <div className="text-sm text-gray-500 mb-1">
                                    {t('reviewTask.detail.phone')}
                                </div>
                                <div className="text-lg font-medium">
                                    {task?.submitterPhone || '--'}
                                </div>
                            </Col>
                            <Col flex="1">
                                <div className="text-sm text-gray-500 mb-1">
                                    {t('reviewTask.detail.appealSubmittedAt')}
                                </div>
                                <div className="text-lg font-medium">
                                    {appeal?.appealSubmittedAt
                                        ? formatEpochToDateTime(appeal.appealSubmittedAt)
                                        : formatEpochToDateTime(task?.submittedAt)}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* 中部两栏：左 订单与客诉信息 / 右 司机申诉信息 */}
                    <Row gutter={24} align="stretch">
                        <Col span={12} style={{display: 'flex'}}>
                            <Card
                                size="small"
                                title={
                                    <span className="text-base font-medium">
                                        {t('reviewTask.detail.orderAndComplaintInfo')}
                                    </span>
                                }
                                style={{width: '100%'}}
                                styles={{body: {padding: '16px 20px'}}}
                            >
                                {/* 子区 1：订单详情 5 字段（状态、收件人、地址、签收时间、司机），
                                    供审核员核实「申诉是否成立」 */}
                                <div className="text-sm font-medium mb-2">
                                    {t('reviewTask.detail.orderDetail')}
                                </div>
                                {orderDetail ? (
                                    <>
                                        <InfoRow label={t('reviewTask.detail.orderStatus')}>
                                            {orderDetail.overallStatus
                                                ? t(`enum.overallStatus.${orderDetail.overallStatus}`, orderDetail.overallStatus)
                                                : '--'}
                                        </InfoRow>
                                        <InfoRow label={t('reviewTask.detail.recipientName')}>
                                            {orderDetail.recipientName || '--'}
                                        </InfoRow>
                                        <InfoRow label={t('reviewTask.detail.deliveryAddress')}>
                                            {orderDetail.deliveryAddress || '--'}
                                        </InfoRow>
                                        <InfoRow label={t('reviewTask.detail.deliveredAt')}>
                                            {formatEpochToDateTime(orderDetail.deliveredAt)}
                                        </InfoRow>
                                        <InfoRow label={t('reviewTask.detail.driverName')}>
                                            {orderDetail.driverName || '--'}
                                        </InfoRow>
                                        {(orderDetail.podImageS3UrlsJson?.length ?? 0) > 0 && (
                                            <InfoRow label={t('reviewTask.detail.pod')}>
                                                <Image.PreviewGroup>
                                                    <Space wrap>
                                                        {orderDetail.podImageS3UrlsJson!.map((url, idx) => (
                                                            <AttachmentTile key={idx} url={url} />
                                                        ))}
                                                    </Space>
                                                </Image.PreviewGroup>
                                            </InfoRow>
                                        )}
                                    </>
                                ) : (
                                    <Text type="secondary" className="text-sm">--</Text>
                                )}

                                <div className="my-4" style={{borderTop: '1px solid #f0f0f0'}} />

                                {/* 子区 2：用户客诉记录 */}
                                <div className="text-sm font-medium mb-2">
                                    {t('reviewTask.detail.complaintRecord')}
                                </div>
                                <InfoRow label={t('reviewTask.detail.complaintCategory')}>
                                    {complaint?.complaintCategory
                                        ? pickCategoryText(
                                              categoryByCode.get(complaint.complaintCategory),
                                              currentLocale,
                                          ) || complaint.complaintCategory
                                        : '--'}
                                </InfoRow>
                                <InfoRow label={t('reviewTask.detail.externalRefCode')}>
                                    {complaint?.externalRefCode ?? '--'}
                                </InfoRow>
                                {complaint?.complaintReason &&
                                pickDisplayText(complaint.complaintReason, currentLocale) ? (
                                    <ComplaintReasonView
                                        reason={complaint.complaintReason}
                                        currentLocale={currentLocale}
                                        label={t('reviewTask.detail.complaintDetail')}
                                        showOriginalLabel={t('reviewTask.detail.showOriginal')}
                                        showTranslationLabel={t('reviewTask.detail.showTranslation')}
                                    />
                                ) : null}
                                {complaintEvidenceUrls.length > 0 && (
                                    <div className="mt-4">
                                        <Text type="secondary" className="text-sm">
                                            {t('reviewTask.detail.complaintAttachments')}:
                                        </Text>
                                        <div className="mt-2">
                                            <Space wrap>
                                                {complaintEvidenceUrls.map((url, idx) => (
                                                    <AttachmentTile key={idx} url={url} />
                                                ))}
                                            </Space>
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </Col>
                        <Col span={12} style={{display: 'flex'}}>
                            <Card
                                size="small"
                                title={
                                    <span className="text-base font-medium">
                                        {t('reviewTask.detail.appealInfo')}
                                    </span>
                                }
                                style={{width: '100%'}}
                                styles={{body: {padding: '16px 20px'}}}
                            >
                                {appeal ? (
                                    <>
                                        {/* 1. 申诉理由（最重要） */}
                                        <div>
                                            <Text type="secondary" className="text-sm">
                                                {t('reviewTask.detail.appealReason')}:
                                            </Text>
                                            <div
                                                className="text-sm mt-1 p-3"
                                                style={{
                                                    background: '#fafafa',
                                                    borderRadius: 4,
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {pickDisplayText(appeal.appealReason, normalizeLang(i18n.language)) || '-'}
                                            </div>
                                        </div>

                                        {/* 2. 上传凭证 */}
                                        {attachments.length > 0 && (
                                            <div className="mt-4">
                                                <Text type="secondary" className="text-sm">
                                                    {t('reviewTask.detail.appealAttachments')}:
                                                </Text>
                                                <div className="mt-2">
                                                    <Space wrap>
                                                        {attachments.map((url, idx) => (
                                                            <AttachmentTile key={idx} url={url} />
                                                        ))}
                                                    </Space>
                                                </div>
                                            </div>
                                        )}

                                        {/* 3. 分隔线 + 申诉人/时间 metadata */}
                                        <div className="my-4" style={{borderTop: '1px solid #f0f0f0'}} />
                                        <InfoRow label={t('reviewTask.detail.appealSubmittedBy')}>
                                            {task?.submitterName || appeal.appealSubmittedBy || '--'}
                                        </InfoRow>
                                        <InfoRow label={t('reviewTask.detail.appealSubmittedAt')}>
                                            {formatEpochToDateTime(appeal.appealSubmittedAt)}
                                        </InfoRow>

                                        {/* 审核结果回显已移至底部审核区（readonly 模式），此处不再重复展示 */}
                                    </>
                                ) : (
                                    <Text type="secondary" className="text-sm">--</Text>
                                )}
                            </Card>
                        </Col>
                    </Row>

                </Space>
            )}

            {/* 审核区：sticky bottom —— 内容少时跟随内容，内容多时滚动保持浮在底部。
                已完结工单也渲染同样的布局，但全部 disabled 并隐藏提交按钮（readonly 回显态） */}
            {(canReview || readonly) && (
                <div
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        marginTop: 16,
                        zIndex: 10,
                        background: '#fff',
                    }}
                >
                    <Card
                        size="small"
                        title={
                            <span className="text-base font-medium">
                                {t('reviewTask.detail.reviewAction')}
                            </span>
                        }
                        styles={{body: {padding: '16px 20px'}}}
                    >
                        {/* 第 1 行：审核结果 radio + 快捷理由（按所选动作切换通过/驳回字典），label 在上 */}
                        <div className="flex items-start gap-8 mb-3 flex-wrap">
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium">
                                    {t('reviewTask.detail.reviewResultLabel')}
                                </span>
                                {/* readonly 不用 disabled——antd disabled 会把选中那个也灰化，看不出原选择。
                                    改用 pointerEvents: none 阻止交互，selected 紫色保留 */}
                                <div style={readonly ? {pointerEvents: 'none'} : undefined}>
                                    <Radio.Group
                                        value={reviewAction}
                                        onChange={(e) => {
                                            setReviewAction(e.target.value);
                                            // 通过/驳回字典互不相通，切换动作时清空已选理由
                                            setSelectedReasonKeys([]);
                                        }}
                                    >
                                        <Radio value="APPROVE">{t('reviewTask.detail.approve')}</Radio>
                                        <Radio value="REJECT">{t('reviewTask.detail.reject')}</Radio>
                                    </Radio.Group>
                                </div>
                            </div>
                            {readonly && (
                                <>
                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm font-medium">
                                            {t('reviewTask.detail.reviewerId')}
                                        </span>
                                        <Text className="text-sm" style={{lineHeight: '32px'}}>
                                            {task?.reviewerName || appeal?.reviewerId || '--'}
                                        </Text>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm font-medium">
                                            {t('reviewTask.detail.reviewedAt')}
                                        </span>
                                        <Text className="text-sm" style={{lineHeight: '32px'}}>
                                            {formatEpochToDateTime(appeal?.reviewedAt)}
                                        </Text>
                                    </div>
                                </>
                            )}
                            {reviewAction && !readonly && (
                                <div className="flex flex-col gap-2">
                                    <span className="text-sm font-medium">
                                        {t(
                                            reviewAction === 'APPROVE'
                                                ? 'reviewTask.detail.quickApproveReasons'
                                                : 'reviewTask.detail.quickRejectReasons'
                                        )}
                                        {reviewAction === 'REJECT' && (
                                            <span className="text-red-500 ml-1">*</span>
                                        )}
                                    </span>
                                    <Space wrap size={[8, 8]}>
                                        {activeReasons.map((option) => {
                                            const selected = selectedReasonKeys.includes(option.code);
                                            const tag = (
                                                <Tag.CheckableTag
                                                    checked={selected}
                                                    onChange={() => toggleReasonKey(option.code)}
                                                    style={{
                                                        padding: '4px 12px',
                                                        fontSize: 14,
                                                        borderRadius: 4,
                                                        border: selected
                                                            ? '1px solid transparent'
                                                            : '1px solid #d9d9d9',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {pickRejectReasonText(option, i18n.language)}
                                                </Tag.CheckableTag>
                                            );
                                            // 判定标准 hover 提示：按 code 匹配前端 i18n，无对应文案（如 OTHER）则不出 Tooltip
                                            const criteriaKey = `reviewTask.detail.reasonCriteria.${option.code}`;
                                            return i18n.exists(criteriaKey) ? (
                                                <Tooltip
                                                    key={option.code}
                                                    title={
                                                        <span style={{whiteSpace: 'pre-line'}}>
                                                            {t(criteriaKey)}
                                                        </span>
                                                    }
                                                    overlayStyle={{maxWidth: 420}}
                                                >
                                                    {tag}
                                                </Tooltip>
                                            ) : (
                                                <React.Fragment key={option.code}>{tag}</React.Fragment>
                                            );
                                        })}
                                    </Space>
                                </div>
                            )}
                        </div>

                        {/* 第 2 行：处理说明（选定动作后显示；通过：Tag 或文本至少其一；驳回：必选 Tag，选「其他」时文本必填） + 提交按钮（仅 editable 显示）。
                            一体化容器：外层 div 模拟 antd 输入框边框；内部顶部为已选 Tag chips（带 × 可删），
                            中间分割线，下方无边框 TextArea。视觉上 Tag 与文本属于同一个评审说明。
                            readonly 模式：有评论内容（理由 Tag 或自由文本）才渲染本行，无内容整行隐藏 */}
                        {(!readonly || readonlyHasComment) && (
                        <div className="flex items-end gap-3">
                            {!!reviewAction && (!readonly || readonlyHasComment) && (
                            <div className="flex flex-col gap-2" style={{flex: 1}}>
                                <span className="text-sm font-medium">
                                    {t('reviewTask.detail.commentLabel')}
                                    {!readonly && (reviewAction === 'APPROVE' || rejectNeedsComment) && (
                                        <span className="text-red-500 ml-1">*</span>
                                    )}
                                </span>
                                <div
                                    className={`comment-composer ${
                                        commentComposerFocused && !readonly ? 'is-focused' : ''
                                    }`}
                                    style={{
                                        border: `1px solid ${
                                            commentComposerFocused && !readonly ? '#7d4cdb' : '#d9d9d9'
                                        }`,
                                        borderRadius: 6,
                                        background: readonly ? '#f5f5f5' : '#fff',
                                        boxShadow: commentComposerFocused && !readonly
                                            ? '0 0 0 2px rgba(125, 76, 219, 0.1)'
                                            : 'none',
                                        transition: 'all 0.2s',
                                        pointerEvents: readonly ? 'none' : 'auto',
                                        cursor: readonly ? 'not-allowed' : 'auto',
                                    }}
                                >
                                    {selectedReasonKeys.length > 0 && (
                                        <>
                                            <div style={{padding: '8px 10px 6px'}}>
                                                <Space wrap size={[6, 6]}>
                                                    {selectedReasonKeys.map((key) => (
                                                        <Tag
                                                            key={key}
                                                            closable={!readonly}
                                                            onClose={(e) => {
                                                                e.preventDefault();
                                                                toggleReasonKey(key);
                                                            }}
                                                            color="purple"
                                                            style={{margin: 0}}
                                                        >
                                                            {pickRejectReasonText(reasonByCode.get(key), i18n.language) || key}
                                                        </Tag>
                                                    ))}
                                                </Space>
                                            </div>
                                            {!readonly && (
                                                <div style={{borderTop: '1px dashed #f0f0f0'}} />
                                            )}
                                        </>
                                    )}
                                    <TextArea
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        onFocus={() => !readonly && setCommentComposerFocused(true)}
                                        onBlur={() => !readonly && setCommentComposerFocused(false)}
                                        readOnly={readonly}
                                        placeholder={
                                            readonly
                                                ? ''
                                                : (t(
                                                      reviewAction === 'REJECT'
                                                          ? 'reviewTask.detail.commentPlaceholderReject'
                                                          : 'reviewTask.detail.commentPlaceholder'
                                                  ) as string)
                                        }
                                        rows={3}
                                        showCount={
                                            readonly
                                                ? false
                                                : {
                                                      formatter: ({count}) => `${count} / ${COMMENT_MAX}`,
                                                  }
                                        }
                                        maxLength={readonly ? undefined : textareaMaxLength}
                                        variant="borderless"
                                        style={{
                                            resize: 'none',
                                            background: 'transparent',
                                            color: readonly ? 'rgba(0,0,0,0.65)' : undefined,
                                            cursor: readonly ? 'not-allowed' : 'auto',
                                        }}
                                    />
                                </div>
                            </div>
                            )}
                            {/* 未选动作时用 flex spacer 把提交按钮推到右侧（选定动作后左侧 composer 已 flex:1 占满，无需 spacer） */}
                            {!reviewAction && !readonly && <div style={{flex: 1}} />}
                            {/* 按钮列：仅待审核时显示。批量模式与 TextArea 同高、跳过/提交平分；单条模式仅显示常规尺寸的提交按钮 */}
                            {!readonly && isBatchMode && !isLastTask ? (
                                <div
                                    className="flex flex-col gap-2 self-stretch"
                                    style={{minWidth: 140}}
                                >
                                    <span className="text-sm font-medium invisible">.</span>
                                    <div className="flex flex-col gap-2 flex-1">
                                        <Button
                                            onClick={handleSkip}
                                            disabled={actionLoading}
                                            className="flex-1"
                                        >
                                            {t('reviewTask.detail.skip')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            loading={actionLoading}
                                            disabled={actionLoading || !reviewAction}
                                            onClick={handleSubmit}
                                            className="flex-1"
                                        >
                                            {t('reviewTask.detail.submitReview')}
                                        </Button>
                                    </div>
                                </div>
                            ) : !readonly ? (
                                <Button
                                    type="primary"
                                    loading={actionLoading}
                                    disabled={actionLoading || !reviewAction}
                                    onClick={handleSubmit}
                                    style={{minWidth: 140}}
                                >
                                    {t('reviewTask.detail.submitReview')}
                                </Button>
                            ) : null}
                        </div>
                        )}
                    </Card>
                </div>
            )}

            {/* 操作日志窄抽屉（点击运单号触发） */}
            <ReviewTaskTimelineDrawer
                open={timelineVisible}
                onClose={() => setTimelineVisible(false)}
                complaint={complaint}
                logs={logs}
            />
        </Drawer>
    );
};

export default ReviewTaskDetailDrawer;
